import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Banknote,
  GraduationCap,
  HeartPulse,
  Landmark,
  Megaphone,
  Scale,
  Shield,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { CandidateAvatar } from '@/components/common/CandidateAvatar/CandidateAvatar';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { useCandidates } from '@/hooks/use-candidates';
import { usePoliticalMatchMatches } from '@/hooks/usePoliticalMatchMatches';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { buildCandidatePhotoPath } from '@/utils/candidatePhoto';
import { formatPoliticalMatchScore } from '@/utils/political-match';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/positions.module.css';

function getQuestionIcon(questionId: string | null | undefined): LucideIcon {
  switch (questionId) {
    case 'economica-impostos-gastos-menores':
    case 'economica-privatizacao-servicos':
    case 'economica-negociacao-trabalhista':
    case 'economica-regulacao-negocios':
    case 'economica-redistribuicao-renda':
    case 'economica-servicos-publicos-ampliados':
    case 'economica-subsidios-direcionamento':
    case 'economica-gestao-privada-melhor':
      return Banknote;
    case 'pessoal-expressao-restricoes':
    case 'pessoal-protestos-ordem-publica':
    case 'pessoal-remocao-conteudo-politico':
      return Megaphone;
    case 'pessoal-privacidade-vigilancia':
    case 'pessoal-seguranca-monitoramento':
    case 'pessoal-limites-comportamento-privado':
      return Shield;
    case 'pessoal-autonomia-vida-privada':
    case 'pessoal-maioria-nao-pode-impor':
    case 'pessoal-minorias-protecao':
    case 'pessoal-liberdade-associacao':
      return Users;
    case 'pessoal-religiao-estado':
    case 'pessoal-moral-tradicional-punicao':
    case 'pessoal-justica-menor-intervencao':
      return Scale;
    case 'pessoal-descriminalizacao-drogas':
    case 'pessoal-saude-em-vez-de-punicao':
      return HeartPulse;
    case 'pessoal-valores-morais-escola':
      return GraduationCap;
    default:
      return Landmark;
  }
}

export function MatchPoliticoCandidatePage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ candidateId: string }>();
  const candidatesState = useCandidates();
  const matchesState = usePoliticalMatchMatches();
  const { state, status, error, questions = [] } = usePoliticalMatchFlow();
  const effectiveMatches = state.matches?.length ? state.matches : matchesState.matches;

  const match = useMemo(
    () => effectiveMatches.find((entry) => entry.candidate.id === params.candidateId) ?? null,
    [effectiveMatches, params.candidateId],
  );

  const selectedCandidateId = params.candidateId ?? '';
  const candidateList = candidatesState.status === 'success' ? candidatesState.candidates : [];
  const selectedMatchIndex = effectiveMatches.findIndex((entry) => entry.candidate.id === selectedCandidateId);
  const previousMatch = selectedMatchIndex > 0 ? effectiveMatches[selectedMatchIndex - 1] ?? null : null;
  const nextMatch = selectedMatchIndex >= 0 && selectedMatchIndex < effectiveMatches.length - 1 ? effectiveMatches[selectedMatchIndex + 1] : null;
  const questionById = useMemo(() => new Map(questions.map((question) => [question.id, question])), [questions]);
  const candidateName = match?.candidate.name ?? params.candidateId ?? 'Detalhes do candidato';

  useSeo({
    title: buildPageTitle(`${candidateName} | Match Político`),
    description: match
      ? `Veja as posições identificadas de ${match.candidate.name} no mapa político e as evidências documentais associadas.`
      : 'Veja as posições identificadas no Match Político.',
    canonicalPath: params.candidateId ? `/match-politico/detalhes/${params.candidateId}` : '/match-politico/resumo',
    indexable: false,
    image: match ? buildCandidatePhotoPath(match.candidate.slug) : undefined,
    imageAlt: match ? `${match.candidate.name} no Match Político` : 'Verde Confirma',
    ogType: 'article',
  });

  if (status === 'loading') {
    return <LoadingState title="Carregando detalhes" messages={['Preparando o candidato', 'Consultando o resultado', 'Abrindo os detalhes']} />;
  }

  if (status === 'error') {
    return <EmptyState tone="danger" title="Não foi possível carregar os detalhes" description={error ?? ''} />;
  }

  if (matchesState.status === 'loading' && !state.matches?.length) {
    return <LoadingState title="Carregando detalhes" messages={['Preparando o candidato', 'Consultando o resultado', 'Abrindo os detalhes']} />;
  }

  if (matchesState.status === 'error' && !state.matches?.length) {
    return <EmptyState tone="danger" title="Não foi possível carregar os detalhes" description={matchesState.error ?? ''} />;
  }

  if (!params.candidateId) {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  if (!match) {
    return <EmptyState tone="warning" title="Candidato não encontrado" description="Abra o resumo e escolha um candidato." />;
  }

  if (!match.candidate.name) {
    return <LoadingState title="Carregando candidato" messages={['Preparando os detalhes', 'Montando os dados', 'Abrindo a visão']} />;
  }

  const visibleCandidates = candidateList.length > 0
    ? candidateList
      : effectiveMatches.map((entry) => ({
        id: entry.candidate.id,
        name: entry.candidate.name,
        party: entry.candidate.party ?? '',
        electoralNumber: entry.candidate.number,
        photoUrl: buildCandidatePhotoPath(entry.candidate.slug),
      }));

  return (
    <section className={styles.sourcePage}>
      <PageBreadcrumb
        items={[
          { label: 'Match Político', to: '/match-politico' },
          { label: 'Resultado', to: '/match-politico/resumo' },
          { label: match.candidate.name },
        ]}
      />

      <StepHeader
        kicker="Detalhes do candidato"
        title={match.candidate.name}
        description="Detalhes consolidado dos candidatos já identificados nas propostas e nas fontes do plano."
        actions={
          <Button type="button" variant="secondary" onClick={() => navigate('/match-politico/resumo')}>
            Voltar
          </Button>
        }
      />

      <Badge tone="warning">Atenção: Análise assistida por IA.</Badge>

      <div className={styles.candidateDirectoryLayout}>
        <Card className={styles.candidateDirectorySidebar}>
          <div className={styles.candidateDirectorySidebarHeader}>
            <p className={styles.candidateDirectorySidebarTitle}>Candidatos</p>
            <p className={styles.candidateDirectorySidebarCount}>{visibleCandidates.length} candidatos</p>
          </div>

          <div className={styles.candidateDirectoryList}>
            {visibleCandidates.map((candidate) => {
              const isSelected = candidate.id === selectedCandidateId;

              return (
                <Button
                  key={candidate.id}
                  to={`/match-politico/detalhes/${candidate.id}`}
                  variant="ghost"
                  className={classNames(styles.candidateDirectoryItem, isSelected && styles.candidateDirectoryItemActive)}
                  ariaLabel={`Abrir detalhes de ${candidate.name}`}
                >
                  <CandidateAvatar
                    alt={candidate.name}
                    imageUrl={candidate.photoUrl ?? ''}
                    name={candidate.name}
                    size="sm"
                    className={styles.candidateDirectoryAvatar}
                  />

                  <span className={styles.candidateDirectoryItemCopy}>
                    <span className={styles.candidateDirectoryItemName}>{candidate.name}</span>
                  </span>

                  <span className={styles.candidateDirectoryItemArrow} aria-hidden="true">
                    ›
                  </span>
                </Button>
              );
            })}
          </div>
        </Card>

        <Card className={styles.candidateDetailCard}>
          <div className={styles.candidateDetailTop}>
            <CandidateAvatar alt={match.candidate.name} imageUrl={buildCandidatePhotoPath(match.candidate.slug)} name={match.candidate.name} size="lg" />

            <div className={styles.candidateDetailCopy}>
              <div className={styles.candidateDetailHeaderRow}>
                <div>
                  <p className={styles.candidateDetailName}>{match.candidate.name}</p>
                  <p className={styles.candidateDetailParty}>{match.candidate.party ?? 'Sem partido informado'}</p>
                </div>

                {match.candidate.number ? <Badge tone="neutral">Número {match.candidate.number}</Badge> : null}
              </div>

              <div className={styles.summaryGrid}>
                <Badge tone="primary">Dinheiro e trabalho: {formatPoliticalMatchScore(match.position.economicScore)}</Badge>
                <Badge tone="primary">Vida pessoal: {formatPoliticalMatchScore(match.position.personalScore)}</Badge>
                <Badge tone="neutral">{match.position.answeredQuestions} de {match.position.totalQuestions} respondidas</Badge>
              </div>

              <p className={styles.candidateDetailAlignment}>{match.position.alignmentLabel ?? 'Sem posição suficiente'}</p>
            </div>
          </div>
          <section className={classNames(styles.questionCard, styles.candidateAnswersSection)} aria-label="Respostas identificadas nas propostas">
            <div className={styles.candidateAnswersHeader}>
              <div>
                <p className={styles.candidateDetailPanelTitle}>Respostas identificadas nas propostas</p>
                <p className={styles.candidateDetailText}>Veja como o candidato se posiciona nos temas selecionados.</p>
              </div>

              <Button to="/match-politico/metodologia" variant="ghost" size="sm">
                Sobre como identificamos
              </Button>
            </div>

            <div className={styles.candidateAnswerList}>
              {match.answers.map((answer) => {
                const question = questionById.get(answer.questionId) ?? null;
                const QuestionIcon = getQuestionIcon(answer.questionId);
                const firstEvidence = answer.evidence[0] ?? null;
                const stanceLabel = answer.stance === null ? 'Sem posição suficiente' : answer.stance > 0 ? 'Tende a concordar' : answer.stance < 0 ? 'Tende a discordar' : 'Neutro';
                const stanceTone = answer.stance === null ? 'neutral' : answer.stance > 0 ? 'success' : answer.stance < 0 ? 'danger' : 'neutral';

                return (
                  <Card key={answer.id} tone="soft" className={styles.candidateAnswerCard}>
                    <div className={styles.candidateAnswerHeader}>
                      <div className={styles.candidateAnswerIcon} aria-hidden="true">
                        <QuestionIcon size={22} strokeWidth={2.1} />
                      </div>

                      <div className={styles.candidateAnswerCopy}>
                        <p className={styles.candidateAnswerQuestion}>{question?.statement ?? answer.questionId}</p>
                        <p className={styles.candidateAnswerSummary}>{`...${(firstEvidence?.excerpt ?? 'Sem justificativa registrada.').trim()}...`}</p>
                      </div>

                      <div className={styles.candidateAnswerMeta}>
                        <Badge tone={stanceTone}>{stanceLabel}</Badge>
                        {firstEvidence ? (
                          <>
                            <p className={styles.candidateAnswerSourceMeta}>Plano de governo • p. {firstEvidence.page}</p>
                            <Button to={firstEvidence.url ?? `/proposta-candidato/${match.candidate.id}`} variant="ghost" size="sm">
                              Ver trecho
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className={styles.candidateAnswerNav}>
              <Button type="button" variant="secondary" size="sm" disabled={!previousMatch} onClick={() => previousMatch && navigate(`/match-politico/detalhes/${previousMatch.candidate.id}`)}>
                Anterior
              </Button>
              <Button type="button" variant="secondary" size="sm" disabled={!nextMatch} onClick={() => nextMatch && navigate(`/match-politico/detalhes/${nextMatch.candidate.id}`)}>
                Próximo
              </Button>
            </div>
          </section>
        </Card>
      </div>
    </section>
  );
}
